import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '@Helpers';
import { GENERIC_TAG, isValidType, masterNameFor, normaliseType } from '@Utils';

import { sendCustomEvent, sendErrorEvent } from '@Analytics';
import { ModalComponent, ModalLoading, ModalSuccess } from '@Components';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@Components/ui-v2/combobox';
import { Input } from '@Components/ui-v2/input';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useMasters } from '@CustomHooks';
import { Options } from '@Models';
import NewMasterSummary from './NewMasterSummary';

interface NewMasterModalProps {
  isOpen: boolean;
  onClose: () => void;
  sources: Options[];
  refetch: () => void;
}

/**
 * A source the data is not tied to.
 *
 * An entry in the list rather than a checkbox beside it: a master
 * belongs to one source or to none, and two controls for one choice let
 * a person pick both.
 */
const GENERIC_OPTION_VALUE = `__${GENERIC_TAG}__`;

const NewMasterModal = ({ isOpen, onClose, sources, refetch }: NewMasterModalProps) => {
  const { t } = useTranslation();
  const { createMaster } = useMasters();
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState<Options | null>(null);
  const [masterType, setMasterType] = useState<string>('');
  const [confirming, setConfirming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const options = useMemo<Options[]>(
    () => [{ value: GENERIC_OPTION_VALUE, label: t('Configuration.GenericSource') }, ...sources],
    [sources, t],
  );

  const isGeneric = source?.value === GENERIC_OPTION_VALUE;
  const sourceName = source && !isGeneric ? source.value : null;

  const composed = masterNameFor(masterType, sourceName);

  useEffect(() => {
    setSource(null);
    setFile(null);
    setMasterType('');
    setConfirming(false);
    setError(null);
  }, [isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.target?.files?.[0]) {
      setFile(e.target.files[0]);
    }
  };

  const onReview = () => {
    setError(null);

    if (!source) {
      setError(t('Configuration.SelectConnectorError'));
      return;
    }
    if (!isValidType(normaliseType(masterType))) {
      setError(t('Configuration.SelectMasterTypeError'));
      return;
    }
    if (!file) {
      setError(t('Configuration.SelectFileError'));
      return;
    }

    setConfirming(true);
  };

  const onConfirm = () => {
    if (!file || !source) return;

    // The id, not the name: the registry addresses a source by it, and
    // the name only decides what this master ends up called.
    const sourceId = isGeneric ? null : source.id ?? null;

    setLoading(true);
    createMaster(file, normaliseType(masterType), sourceId)
      .then(() => {
        onClose();
        setLoading(false);
        setSuccess(true);
        refetch();
        sendCustomEvent('CONFIGURATION_FILE_UPLOADED', {
          type: 'UPLOAD',
          newFileName: composed.displayName,
          connector: sourceName ?? GENERIC_TAG,
        });
        showSuccessToast(t('Configuration.Success'));
      })
      .catch((failure: Error) => {
        setLoading(false);
        setSuccess(false);
        setConfirming(false);
        sendErrorEvent(`unable to create master. ${failure.message}`);
        showErrorToast(failure.message);
      });
  };

  const form = (
    <div className="w-full flex items-center flex-col gap-6 max-w-full">
      <div className="w-full">
        <span className="text-gray-900 text-sm" id="connector">
          {`${t('Configuration.Connector')}*`}
        </span>
        <Combobox
          items={options}
          value={source}
          onValueChange={(item: Options | null) => setSource(item)}
          itemToStringLabel={(item: Options) => item.label}>
          <ComboboxInput placeholder={t('Configuration.SelectConnector')} />
          <ComboboxContent>
            <ComboboxEmpty>{t('Configuration.SelectConnector')}</ComboboxEmpty>
            <ComboboxList>
              {(item: Options) => (
                <ComboboxItem key={item.value} value={item}>
                  {item.label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
        <p className="text-gray-500 text-xs mt-1">{t('Configuration.SourceHelper')}</p>
      </div>

      <div className="w-full">
        <span className="text-gray-900 text-sm" id="masterType">
          {`${t('Configuration.MasterType')}*`}
        </span>
        <Input
          type="text"
          placeholder={t('Configuration.SelectMasterType')}
          value={masterType}
          onChange={(e) => setMasterType(e.target.value)}
          className="text-base"
        />
      </div>

      <div className="flex flex-col gap-1 w-full">
        <label
          htmlFor="file-input"
          className="flex items-center justify-center p-2 rounded-md text-sm font-semibold w-auto border-solid border-gray-300 shadow text-gray-700 hover:border-bg-gray-300 border hover:bg-gray-50 cursor-pointer hover:shadow-sm">
          {t('Configuration.ChooseFile')}

          <input
            id="file-input"
            type="file"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </label>
        {file && (
          <span className="text-gray-500 text-xs">
            {file.name} ({(file.size / 1024).toFixed(2)} KB)
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-3 items-center w-full justify-center">
        <TrackedButton
          variant="outline"
          buttonName="UPLOAD_CONFIGURATION_FILE_CANCELED"
          onClick={onClose}
          isSubmit={false}
          className="w-full bg-white hover:border-bg-gray-300 hover:bg-gray-50 cursor-pointer hover:shadow-sm">
          <span className="flex items-center justify-center">{t('Configuration.Cancel')}</span>
        </TrackedButton>
        <TrackedButton
          buttonName="UPLOAD_CONFIGURATION_FILE_REVIEW"
          isSubmit={false}
          onClick={onReview}
          className="w-full">
          <span className="flex items-center justify-center">{t('Configuration.Continue')}</span>
        </TrackedButton>
      </div>
    </div>
  );

  return (
    <>
      <ModalComponent
        isOpen={isOpen}
        onClose={onClose}
        title={confirming ? t('Configuration.ConfirmTitle') : t('Configuration.ModalTitle')}
        closeClickOutside={false}>
        {confirming ? (
          <NewMasterSummary
            composed={composed}
            sourceLabel={isGeneric ? t('Configuration.GenericSource') : source?.label ?? ''}
            fileName={file?.name ?? ''}
            onBack={() => {
              setConfirming(false);
              setError(null);
            }}
            onConfirm={onConfirm}
          />
        ) : (
          form
        )}
      </ModalComponent>

      <ModalLoading isOpen={loading} onClose={() => setLoading(false)}>
        <div className="text-center -mt-[9px]">{t('Configuration.LoadingModal')}</div>
      </ModalLoading>

      <ModalSuccess isOpen={!!success} onClose={() => setSuccess(false)}>
        <div className="text-center">
          <span className="font-semibold text-lg"> {t('Configuration.SuccessModal')} </span>
        </div>
      </ModalSuccess>
    </>
  );
};

export default NewMasterModal;
